import { useState, useEffect, useCallback } from 'react'
import { useAuth }  from '@/auth/AuthContext'
import { useT }     from '@/i18n/I18nContext'
import { LANG_META } from '@/i18n/translations'

const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

const DEFAULT_SETTINGS = {
  language:        'en',
  printSize:       'a4-portrait',
  showLogos:       true,
  showFooter:      true,
  showPageNumbers: true,
  theme:           'light',
};

function LoadingPane() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '60vh', gap: 13, flexDirection: 'column' }}>
      <div style={{ width: 24, height: 24, border: '3px solid var(--sky)',
        borderTop: '3px solid transparent', borderRadius: '50%',
        animation: 'spin .7s linear infinite' }} />
      <div style={{ fontSize: 13, color: 'var(--mist)', fontWeight: 600 }}>Loading settings…</div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SettingsView({ lang, onLangChange }) {
  const t                 = useT();
  const { getAuthHeader } = useAuth();

  const [settings,  setSettings]  = useState(null);   // null = loading
  const [saveMsg,   setSaveMsg]   = useState('');
  const [saving,    setSaving]    = useState(false);

  const showMsg = (msg, ms = 2500) => { setSaveMsg(msg); setTimeout(() => setSaveMsg(''), ms); };

  // ── Load settings from API ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API}/settings`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) {
          setSettings({ ...DEFAULT_SETTINGS, ...data.settings });
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      } catch {
        setSettings(DEFAULT_SETTINGS);
      }
    })();
  }, []);

  // ── Persist to API ────────────────────────────────────────────────────
  const updateSettings = useCallback(async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);           // optimistic update
    setSaving(true);
    try {
      const res  = await fetch(`${API}/settings`, {
        method:  'PUT',
        headers: getAuthHeader(),
        body:    JSON.stringify(next),
      });
      const data = await res.json();
      if (data.success) {
        setSettings({ ...DEFAULT_SETTINGS, ...data.settings });
        showMsg('✅ Saved');
      } else {
        showMsg('⚠️ Save failed');
      }
    } catch {
      showMsg('⚠️ Network error');
    }
    setSaving(false);
  }, [settings, getAuthHeader]);

  // ── Reset all user data ───────────────────────────────────────────────
  const resetAll = async () => {
    if (!window.confirm(t('sett.reset_confirm'))) return;
    try {
      await fetch(`${API}/settings/reset`, { method: 'POST', headers: getAuthHeader() });
    } catch {}
    window.location.reload();
  };

  if (!settings) return <LoadingPane />;

  return (
    <div style={{ padding: '26px 26px 80px', maxWidth: 620, margin: '0 auto', position: 'relative' }}>

      {/* Toast */}
      {saveMsg && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#1a1a2e',
          color: 'white', padding: '11px 20px', borderRadius: 10, fontSize: 13,
          fontWeight: 700, zIndex: 400, boxShadow: '0 8px 28px rgba(0,0,0,.25)' }}>
          {saveMsg}
        </div>
      )}

      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 23, fontWeight: 900,
        color: 'var(--navy)', marginBottom: 3 }}>{t('sett.title')}</div>
      <div style={{ fontSize: 13, color: 'var(--mist)', marginBottom: 22 }}>{t('sett.subtitle')}</div>

      {[
        // ── Language ────────────────────────────────────────────────
        {
          title: t('sett.language'), icon: '🌐',
          content: (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {['en','ml','hi','ta','te'].map(l => (
                <button key={l}
                  onClick={() => { onLangChange(l); updateSettings({ language: l }); }}
                  style={{ padding: '8px 16px',
                    border: lang === l ? '2px solid var(--sky)' : '1.5px solid var(--border)',
                    borderRadius: 9,
                    background: lang === l ? '#EBF4FB' : 'white',
                    color: lang === l ? 'var(--sky)' : '#666',
                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span>{LANG_META[l]?.flag}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{LANG_META[l]?.nativeName}</div>
                    <div style={{ fontSize: 9, color: 'var(--mist)' }}>{l.toUpperCase()}</div>
                  </div>
                  {lang === l && <span style={{ marginLeft: 6, color: 'var(--sky)' }}>✓</span>}
                </button>
              ))}
            </div>
          ),
        },

        // ── Print size ──────────────────────────────────────────────
        {
          title: t('sett.print_size'), icon: '🖨️',
          content: (
            <div style={{ display: 'flex', gap: 7 }}>
              {[[t('sett.a4_portrait'), 'a4-portrait'], [t('sett.a4_landscape'), 'a4-landscape']].map(([label, val]) => (
                <button key={val} onClick={() => updateSettings({ printSize: val })}
                  style={{ padding: '7px 16px',
                    border: settings.printSize === val ? '2px solid var(--sky)' : '1.5px solid var(--border)',
                    borderRadius: 8,
                    background: settings.printSize === val ? '#EBF4FB' : 'white',
                    color: settings.printSize === val ? 'var(--sky)' : '#666',
                    fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          ),
        },

        // ── PDF options ─────────────────────────────────────────────
        {
          title: t('sett.pdf_options'), icon: '📄',
          content: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              {[
                [t('sett.show_logos'),  'showLogos'],
                [t('sett.show_footer'), 'showFooter'],
                [t('sett.show_pages'),  'showPageNumbers'],
              ].map(([lbl, k]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settings[k]}
                    onChange={e => updateSettings({ [k]: e.target.checked })}
                    style={{ width: 16, height: 16, accentColor: 'var(--sky)' }} />
                  <span style={{ fontSize: 13, color: '#555' }}>{lbl}</span>
                </label>
              ))}
            </div>
          ),
        },

        // ── Storage info ────────────────────────────────────────────
        {
          title: t('sett.storage_info'), icon: '💾',
          content: (
            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.9 }}>
              <div>{t('sett.subtitle')}</div>
              <div style={{ marginTop: 8, background: 'var(--fog)', borderRadius: 8,
                padding: '10px 14px', fontSize: 11 }}>
                <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>API Endpoints</div>
                {[
                  'GET/PUT  /api/settings',
                  'GET      /api/children',
                  'GET/PUT  /api/progress/:childId',
                  'GET/POST /api/srt',
                  'GET/POST /api/flashcard-sessions',
                  'GET      /api/collection',
                ].map(line => (
                  <div key={line} style={{ color: 'var(--mist)', fontFamily: 'monospace', fontSize: 10,
                    marginBottom: 2 }}>{line}</div>
                ))}
              </div>
            </div>
          ),
        },

        // ── Danger zone ─────────────────────────────────────────────
        {
          title: t('sett.danger_zone'), icon: '⚠️',
          content: (
            <div>
              <div style={{ fontSize: 13, color: '#555', marginBottom: 11 }}>
                {t('sett.reset_desc')}
              </div>
              <button onClick={resetAll}
                style={{ padding: '9px 18px', background: 'var(--red)', color: 'white',
                  border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {t('sett.reset_all')}
              </button>
            </div>
          ),
        },

      ].map((section, i) => (
        <div key={section.title} className="fu"
          style={{ animationDelay: `${i * 50}ms`, background: 'white', borderRadius: 12,
            padding: '17px 19px', border: '1.5px solid var(--border)', marginBottom: 11 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}>
            <span style={{ fontSize: 18 }}>{section.icon}</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{section.title}</span>
            {saving && i < 3 && (
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--mist)' }}>saving…</span>
            )}
          </div>
          {section.content}
        </div>
      ))}
    </div>
  );
}